import React, { useState, useEffect } from 'react';
import { 
    Modal, 
    Steps, 
    Select, 
    Button, 
    Result, 
    Spin, 
    Typography,
    Space
} from 'antd';
import { 
    CheckCircleFilled, 
    CloseCircleFilled 
} from '@ant-design/icons';
import { integrateCronofy } from '../../reducers/integrations/resources';
import mixpanel from '../../utils/mixpanel';

const { Step } = Steps;
const { Option } = Select;
const { Text, Paragraph } = Typography;

const CronofyCalendarModal = ({ 
    open, 
    onCancel, 
    calendars = [], 
    cronofyData, 
    onSuccess 
}) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedCalendarId, setSelectedCalendarId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (open) {
            setCurrentStep(0);
            setSelectedCalendarId(null);
            setIsLoading(false);
            setIsError(false);
            setErrorMessage('');
        }
    }, [open]);

    const handleIntegrate = async () => {
        if (!selectedCalendarId || !cronofyData) return;

        mixpanel.track(
            'integrations:integrate cronofy',
            { data: { appId: cronofyData.appId } }
        );

        setCurrentStep(1);
        setIsLoading(true);

        try {
            const { userId, appId, tokenData } = cronofyData;
            const data = {
                userId,
                appId,
                calendarId: selectedCalendarId,
                tokenData,
            };

            const integrationResult = await integrateCronofy(data);
            
            if (integrationResult.data && integrationResult.data.isError) {
                throw new Error('Integration returned validation error');
            }

            setIsLoading(false);
            setCurrentStep(2);
            setIsError(false);
        } catch (error) {
            console.error(error);
            setIsLoading(false);
            setCurrentStep(2);
            setIsError(true);
            setErrorMessage(error.message || 'There was a problem with the integration.');
        }
    };

    const handleClose = () => {
        if (currentStep === 2 && !isError) {
            onSuccess();
        } else {
            onCancel();
        }
    };

    const renderContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div style={{ padding: '24px 0', textAlign: 'center' }}>
                        <Paragraph>Choose the calendar to integrate to</Paragraph>
                        <Select
                            style={{ width: '100%', maxWidth: 300, textAlign: 'left' }}
                            placeholder="Select a calendar"
                            value={selectedCalendarId}
                            onChange={setSelectedCalendarId}
                        >
                            {calendars
                                .filter(cal => !cal.calendar_readonly)
                                .map(cal => (
                                    <Option key={cal.calendar_id} value={cal.calendar_id}>
                                        {cal.calendar_name}
                                    </Option>
                                ))
                            }
                        </Select>
                    </div>
                );
            case 1:
                return (
                    <div style={{ padding: '48px 0', textAlign: 'center' }}>
                        <Spin size="large" />
                        <Paragraph style={{ marginTop: 24 }}>
                            Please wait while we validate the connection...
                        </Paragraph>
                    </div>
                );
            case 2:
                if (isError) {
                    return (
                        <Result
                            status="error"
                            title="Connection Failed"
                            subTitle={
                                <>
                                    <Paragraph>
                                        There seems to be a connection issue with your calendar.
                                    </Paragraph>
                                    <Paragraph type="secondary">
                                        {errorMessage}
                                    </Paragraph>
                                    <Paragraph>
                                        We’ve sent a notification to the GoHire support team to look into it. 
                                        You will be copied on the Support Request email.
                                    </Paragraph>
                                </>
                            }
                        />
                    );
                }
                return (
                    <Result
                        status="success"
                        title="Calendar Connected!"
                        subTitle="Hooray! Your calendar is now connected to GoHire. You can now start texting candidates to self-select times for an interview or pre-screening call."
                    />
                );
            default:
                return null;
        }
    };

    const renderFooter = () => {
        if (currentStep === 0) {
            return [
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button 
                    key="next" 
                    type="primary" 
                    disabled={!selectedCalendarId} 
                    onClick={handleIntegrate}
                >
                    Next
                </Button>
            ];
        }
        if (currentStep === 2) {
            return [
                <Button key="close" type="primary" onClick={handleClose}>
                    Close
                </Button>
            ];
        }
        return null; // No footer for step 1 (loading)
    };

    return (
        <Modal
            title="Calendar Integration"
            open={open}
            onCancel={currentStep === 1 ? null : handleClose}
            footer={renderFooter()}
            maskClosable={false}
            closable={currentStep !== 1}
            width={600}
        >
            <div style={{ marginTop: 24 }}>
                <Steps current={currentStep}>
                    <Step title="Select Calendar" />
                    <Step title="Integration" />
                    <Step title="Done" />
                </Steps>
                
                <div style={{ minHeight: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    {renderContent()}
                </div>
            </div>
        </Modal>
    );
};

export default CronofyCalendarModal;
